import React, { useEffect, useState } from "react";

import { Header } from "components/Header";
import { SearchBar } from "components/SearchBar";
import { StepRegisterProvider } from "components/Modals/StepRegister/StepRegisterProvider";
import { StepRegisterManyProvider } from "components/Modals/StepRegister/StepRegisterManyProvider";
import { DataTable } from "components/DataTable";
import { AlertModal } from "components/Modals/AlertModal";

import {
  handleProviderActiveSubmit,
  handleProviderDeleteSubmit,
} from "services/handles/ProviderHandles";

import { IProvider } from "interfaces/Provider";

import { DashboardTemplate } from "templates/DashboardTemplate";

import { useProviderRegister } from "contexts/register/ProviderContext";

import { IFilterWhereOptions, useDataList } from "hooks/useDataList";

import { CheckAlert } from "utils/checkAlert";
import debouncer from "utils/debouncer";
import { useAuth } from "contexts/authContext";

export function Providers() {
  const [registerModal, setRegisterModal] = useState(false);
  const [activationModal, setActivationModal] = useState(false);
  const [provider, setProvider] = useState<IProvider>();
  const { role } = useAuth()
  const [deleteModal, setDeleteModal] = useState(false);

  const { storeProvider, hasChanges, setHasChanges } = useProviderRegister();
  const [page, setPage] = useState(1);

  const [isAlertFetching, setIsAlertFetching] = useState<boolean>(false);

  const [filterWhere, setFilterWhere] = useState<IFilterWhereOptions<IProvider>>()

  const [isRegisterManyProviderModalOpen, setIsRegisterManyProviderModalOpen] =
    useState<boolean>(false);

  const { data, isFetching } = useDataList<IProvider>({
    queryKey: 'listAllProviders',
    route: 'providers',
    filter: {
      size: 10,
      page,
      params: filterWhere,
      relations: { user: true },
      order: {
        name:"ASC"
      }
    }
  })

  function handleMenuClick(menuItemId: string, data: IProvider) {
    setProvider(data);

    switch (menuItemId) {
      case 'activate':
        setActivationModal(!activationModal);
        break;
      case 'deactivate':
        setDeleteModal(!deleteModal);
        break;
      case 'edit':
        storeProvider(data);
        setRegisterModal(!registerModal);
        break;
    }
  }

  function handleSearchChange(queryString: string) {
    if (queryString) {
      return setFilterWhere([
        { name: `%${queryString}%` },
        { managerName: `%${queryString}%` },
        { tradingName: `%${queryString}%` },
        { managerName: `%${queryString}%` },
        { user: { name: `%${queryString}%` } },
      ])
    }
  }

  const debounceProviderSearch = debouncer(handleSearchChange, 800);

  useEffect(() => {
    CheckAlert();
  }, []);

  CheckAlert();

  return (
    <DashboardTemplate
      hasTableWrapper
      total={data?.total || 0}
      page={page}
      setPage={setPage}
    >
      <Header title="Fornecedores" />
      <SearchBar
        type="text"
        name="filter"
        id="filter"
        placeholder="Procure por nome, CNPJ, produto..."
        buttonText="Cadastrar fornecedor"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            debounceProviderSearch(event.target.value)
          } else {
            setFilterWhere(undefined);
          }
        }}
        required={false}
        onClick={() => setRegisterModal(true)}
        secondButton={true}
        secondButtonText="Cadastro em lote"
        secondOnClick={() => setIsRegisterManyProviderModalOpen(true)}
      />

      <StepRegisterManyProvider
        opened={isRegisterManyProviderModalOpen}
        setOpened={setIsRegisterManyProviderModalOpen}
      />

      <DataTable 
        data={data?.items}
        isFetching={isFetching}
        headers={[
          { field: 'managerName', label: 'Nome do fornecedor' },
          { field: 'active', label: 'Status do registro', type: 'status' },
          { field: 'user', label: 'CNPJ / CPF', renderColumn: (data) => data.user.cnpj || data.user.cpf },
          { field: 'name', label: 'Nome fantasia' },
          { field: 'tradingName', label: 'Razão social' },
          { field: 'user', label: 'E-mail', renderColumn: (data) => data.user.email },
        ]}
        menuActions={[
          { id: 'activate', label: 'Ativar fornecedor', onClick: handleMenuClick, hide: (data) => role === "master" && !data.active },
          { id: 'deactivate', label: 'Desativar fornecedor', onClick: handleMenuClick, hide: (data) =>role === "master" && data.active },
          { id: 'edit', label: 'Editar informações', onClick: handleMenuClick },
        ]}
      />

      <AlertModal 
        isOpen={deleteModal}
        onRequestClose={() => setDeleteModal(false)}
        title={`Deseja desativar o fornecedor ${provider?.name}?`}
        text="Um fornecedor desativado não poderá ser associado a novos serviços. 
          Os serviços que eles possuem também são desativados, e por cascata, 
          os produtos também."
        confirmButtonText="Sim, desativar"
        isConfirmFetching={isAlertFetching}
        confirmButtonClick={() => {
          setIsAlertFetching(true);
          handleProviderDeleteSubmit({
            provider,
            setModal: setDeleteModal,
          })
        }}
      />

      <AlertModal 
        isOpen={activationModal}
        onRequestClose={() => setActivationModal(false)}
        title={`Deseja ativar o fornecedor ${provider?.name}?`}
        text="Um fornecedor ativado  poderá 
          ser associado a novos serviços. Os serviços 
          que eles possuem também são ativados."
        confirmButtonText="Sim, ativar"
        isConfirmFetching={isAlertFetching}
        confirmButtonClick={() => {
          setIsAlertFetching(true);
          handleProviderActiveSubmit({
            provider,
            setModal: setActivationModal,
          })
        }}
      />

      {registerModal && (
        <StepRegisterProvider
          opened={registerModal}
          setOpened={() => {
            setProvider(undefined);
            setRegisterModal(false);

            if (hasChanges) {
              setHasChanges(false);
              window.location.reload();
            }
          }}
          providerData={provider}
        />
      )}
    </DashboardTemplate>
  );
}
